/* eslint-disable no-console */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Chip, CircularProgress, makeStyles, Typography, TextField, Select, MenuItem, FormControl, InputLabel, FormHelperText, Box } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useHistory, useRouteMatch } from 'react-router-dom';

import QuizMaterials from './QuizMaterials';
import QuizObservations from './QuizObservations';
import BackButton from '../../components/common/BackButton';
import TagsTextField from '../../components/common/TagsTextField';
import SendToTechsModal from '../../components/observation/SendToTechsModal';
import { quizCollectionPrivacy, quizCollectionPrivacyList } from '../../models/QuizCollection';
import QuizCollectionService from '../../services/quizCollection.service';

const quizCollectionService = new QuizCollectionService();

const useStyle = makeStyles((theme) => {
  return {
    buttonAction: {
      margin: '4px',
      paddingLeft: '20px',
      paddingRight: '20px',
    },
    buttonsContainer: {
      alignItems: 'center',
      backgroundColor: '#fff',
      display: 'flex',
      gap: theme.spacing(1),
      justifyContent: 'flex-end',
      padding: '16px 0 24px',
    },
    fieldsContainer: {
      '& .MuiFormControl-root input, & .MuiFormControl-root textarea, & .MuiFormControl-root .MuiSelect-selectMenu, & .MuiAutocomplete-input': {
        paddingLeft: '10px !important',
      },
      '& .MuiFormControl-root label': {
        color: '#172B4D',
        fontSize: '14px !important',
        fontWeight: '600',
        transform: 'none',
      },

      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      paddingRight: '8px',
      width: '100%',
    },
    headerDescription: {
      color: theme.palette.grey['500'],
      fontSize: '12px',
    },
    leftPanel: {
      borderRight: '1px solid #ccc',
      paddingRight: 50,
      width: 'calc(100% - 50px)',
    },
    mainContainer: {
      maxHeight: 'calc(100vh - 144px)',
      overflowX: 'hidden',
      overflowY: 'hidden',
      padding: '40px 4rem 0 4rem',
      position: 'relative',
    },
    mainContent: {
      display: 'grid',
      gap: 40,
      gridAutoFlow: 'row',
      gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
      marginRight: 16,
      overflowX: 'hidden',
      overflowY: 'auto',
      paddingRight: 16,
    },
    panelHeader: {
      fontSize: 20,
      fontWeight: 'bold',
      marginBottom: 24,
      marginTop: 36,
    },
    rightPanel: {
      alignItems: 'flex-start',
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      minWidth: 'min(700px, 100%)',
    },
    tagsContainer: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      gap: 4,
      justifyContent: 'start',
      marginTop: '12px',
    },
    tagsLabel: {
      color: theme.palette.grey['500'],
      fontSize: '12px',
      fontWeight: '600',
      marginRight: '8px',
      textTransform: 'uppercase',
    },
    tagsTextField: {
      marginTop: '12px',
    },
    tagsTopLabel: {
      fontSize: 14,
      fontWeight: '600',
      textAlign: 'left',
    },
  };
});

const QuizDetailsView = () => {
  const classes = useStyle();
  const { enqueueSnackbar } = useSnackbar();

  const history = useHistory();
  const match = useRouteMatch();

  const [quizCollection, setQuizCollection] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [formData, setFormData] = useState({ description: '', privacy: '', title: '' });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isTagsLoading, setIsTagsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tags, setTags] = useState([]);
  const [sendToTechsModalOpen, setSendToTechsModalOpen] = useState(false);
  const [materialIds, setMaterialIds] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [observationIds, setObservationIds] = useState([]);

  const quizCollectionId = match.params.id;

  const fetchQuizCollection = useCallback(
    async (id) => {
      try {
        setIsLoading(true);

        const response = await quizCollectionService.getQuizCollectionById(id);

        setQuizCollection(response.quizCollection);
        setSelectedTags(response.quizCollection?.tags || []);
        setFormData({
          description: response.quizCollection?.description || '',
          privacy: response.quizCollection?.privacy || '',
          title: response.quizCollection?.title || '',
        });

        if (response.quizCollection?.id) {
          try {
            const materialsResponse = await quizCollectionService.getQuizCollectionMaterials(response.quizCollection.id);
            const fetchedMaterials = materialsResponse.materials || [];

            const transformedMaterials = fetchedMaterials.map((material, index) => ({
              id: material.id,
              material,
              materialId: material.id,
              order: index,
              resource: material.resource,
              title: material.title,
            }));

            setMaterials(transformedMaterials);
          } catch (materialsError) {
            console.error('Error fetching materials:', materialsError);
            setMaterials([]);
          }
        }
      } catch (error) {
        console.error('Error fetching quiz collection:', error);

        enqueueSnackbar('Failed to load quiz collection', { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  const fetchTags = useCallback(async () => {
    try {
      setIsTagsLoading(true);

      const response = await quizCollectionService.getAllTags();

      setTags(response.tags || []);
    } catch (error) {
      console.error('Error fetching tags:', error);

      enqueueSnackbar('Failed to load tags', { variant: 'error' });
      setTags([]);
    } finally {
      setIsTagsLoading(false);
    }
  }, [enqueueSnackbar]);

  const isNewQuizCollection = useMemo(() => {
    return match.params.id === 'new';
  }, [match.params.id]);

  const handleFormFieldChange = useCallback(
    (fieldName, value) => {
      setFormData((prev) => ({ ...prev, [fieldName]: value }));

      if (formErrors[fieldName]) {
        setFormErrors((prev) => ({ ...prev, [fieldName]: '' }));
      }
    },
    [formErrors]
  );

  const handleSaveClick = useCallback(async () => {
    try {
      const errors = {};
      if (!formData.title?.trim()) {
        errors.title = 'Quiz name is required';
      }
      if (!formData.privacy) {
        errors.privacy = 'Privacy setting is required';
      }
      if (!observationIds || observationIds.length === 0) {
        enqueueSnackbar('Please add at least one case', { variant: 'error' });
        return;
      }

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }

      setFormErrors({});
      const data = {
        ...formData,
        materialIds,
        qualityObservationIds: observationIds,
        tags: selectedTags,
      };
      setIsSaving(true);

      if (isNewQuizCollection) {
        const response = await quizCollectionService.createQuizCollection(data);

        if (response.quizCollection?.id) {
          enqueueSnackbar('Quiz collection created successfully', { variant: 'success' });

          history.push(`/quizzes/${response.quizCollection.id}`);
        }
      } else {
        await quizCollectionService.updateQuizCollection(match.params.id, data);
        enqueueSnackbar('Quiz collection updated successfully', { variant: 'success' });

        await fetchQuizCollection(match.params.id);
      }
    } catch (error) {
      console.error('Error saving quiz collection:', error);

      enqueueSnackbar('Failed to save quiz collection', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [isNewQuizCollection, match.params.id, history, enqueueSnackbar, fetchQuizCollection, selectedTags, formData, materialIds, observationIds]);

  const handleTagsChange = useCallback((newSelectedTags) => {
    setSelectedTags(newSelectedTags);
  }, []);

  const handleRemoveTag = useCallback((tagToRemove) => {
    setSelectedTags((prev) => prev.filter((tag) => tag !== tagToRemove));
  }, []);

  const handleMaterialsUpdate = useCallback((newMaterials) => {
    setMaterials(newMaterials);

    const newMaterialIds = newMaterials.map((m) => m.material?.id || m.materialId).filter(Boolean);

    setMaterialIds(newMaterialIds);
  }, []);

  const handleObservationsChange = useCallback((newObservationIds) => {
    setObservationIds(newObservationIds);
  }, []);

  const handleOpenSendToTechsModal = useCallback(() => {
    setSendToTechsModalOpen(true);
  }, []);

  const handleCloseSendToTechsModal = useCallback(() => {
    setSendToTechsModalOpen(false);
  }, []);

  const renderTagsField = useCallback(() => {
    return (
      <div>
        <Typography className={classes.tagsTopLabel}>Quiz Tags</Typography>

        <div className={classes.tagsTextField}>
          <TagsTextField disabled={isTagsLoading} tags={tags} selectedTags={selectedTags} onChange={handleTagsChange} />
        </div>

        {selectedTags.length > 0 && (
          <div className={classes.tagsContainer}>
            {selectedTags.map((tag) => (
              <Chip
                key={`${tag}-${Math.random()}`}
                label={tag}
                size="small"
                color="primary"
                style={{ marginBottom: '4px', padding: '2px 8px' }}
                onDelete={() => handleRemoveTag(tag)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }, [isTagsLoading, tags, selectedTags, handleTagsChange, handleRemoveTag, classes]);

  useEffect(() => {
    if (!isNewQuizCollection) {
      fetchQuizCollection(match.params.id);
    } else {
      setQuizCollection({
        description: '',
        privacy: quizCollectionPrivacy.WORKSPACE,
        title: '',
      });
      setSelectedTags([]);
      setFormData({
        description: '',
        privacy: quizCollectionPrivacy.WORKSPACE,
        title: '',
      });
    }

    fetchTags();
  }, [isNewQuizCollection, match.params.id, fetchQuizCollection, fetchTags]);

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-between" className={classes.mainContainer}>
      <Box width="100%" className={classes.mainContent}>
        <Box className={classes.leftPanel} display="flex" flexDirection="column" alignItems="flex-start">
          <BackButton route="/quizzes" goBackHistory />

          {quizCollection?.id && (
            <Box alignItems="flex-start">
              <Typography className="page-header mb-0">{quizCollection.title}</Typography>
            </Box>
          )}

          {isNewQuizCollection && (
            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <Typography className="page-header mb-0">Create New Quiz</Typography>

              <Typography className={`mb-0 ${classes.headerDescription}`}>
                Please provide details for your training and select the required resources and supplementary observations
              </Typography>
            </Box>
          )}

          <div className={classes.fieldsContainer}>
            <TextField
              fullWidth
              label="Quiz Name"
              value={formData.title}
              onChange={(e) => handleFormFieldChange('title', e.target.value)}
              placeholder="Quiz Title"
              disabled={isLoading}
              error={!!formErrors.title}
              helperText={formErrors.title}
              margin="normal"
              required
            />

            <TextField
              className="textarea-field"
              fullWidth
              label="Quiz Description"
              value={formData.description}
              onChange={(e) => handleFormFieldChange('description', e.target.value)}
              placeholder="Quiz Description"
              disabled={isLoading}
              error={!!formErrors.description}
              helperText={formErrors.description}
              margin="normal"
              multiline
              minRows={6}
              maxRows={10}
            />

            {renderTagsField()}

            <FormControl fullWidth margin="normal" error={!!formErrors.privacy}>
              <InputLabel>Privacy</InputLabel>
              <Select value={formData.privacy} onChange={(e) => handleFormFieldChange('privacy', e.target.value)} disabled={isLoading} required>
                {quizCollectionPrivacyList.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>

              {formErrors.privacy && <FormHelperText>{formErrors.privacy}</FormHelperText>}
            </FormControl>
          </div>
        </Box>

        <div className={classes.rightPanel}>
          <Typography variant="h6" className={classes.panelHeader}>
            Add Material
          </Typography>

          <QuizObservations quizCollection={quizCollection} isLoading={isLoading} onObservationsChange={handleObservationsChange} />

          <QuizMaterials isLoading={isLoading} quizCollection={quizCollection} onMaterialsChange={handleMaterialsUpdate} initialMaterials={materials} />
        </div>
      </Box>

      <div className={classes.buttonsContainer}>
        {!isNewQuizCollection && (
          <Button variant="outlined" color="primary" onClick={handleOpenSendToTechsModal} disabled={isLoading}>
            Email it to Technologists
          </Button>
        )}

        {!isNewQuizCollection && (
          <Button variant="outlined" color="secondary" onClick={() => history.push(`/quiz/${quizCollectionId}`)} disabled={isLoading}>
            Preview Quiz
          </Button>
        )}

        <Button className={classes.buttonAction} variant="contained" color="primary" onClick={handleSaveClick} disabled={isSaving || isLoading}>
          {isSaving && <CircularProgress size={20} />}

          {!isSaving && isNewQuizCollection ? 'Create Quiz' : 'Update Quiz'}
        </Button>
      </div>

      <SendToTechsModal open={sendToTechsModalOpen} onClose={handleCloseSendToTechsModal} quizCollectionId={quizCollectionId} />
    </Box>
  );
};

export default QuizDetailsView;
